<template>
  <div :class="containerClass">
    <div class="input-and-slots-wrapper" :style="styles">
      <div class="prepend-slot">
        <slot name="prepend"></slot>
      </div>
      <input
        id="inputID"
        :ref="inputRef"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autofocus="autofocus"
        :required="required"
        :value="modelValue"
        @input="emitModelValueUpdate"
        @change="emitModelValueUpdate"
        @focus="onFocus"
        @blur="onBlur"
        @keyup.enter="onKeyupEnter"
        @keyup="onKeyup"
      />
      <div class="append-slot">
        <slot name="append"> </slot>
      </div>
    </div>
    <p class="error-message" :class="{ 'hide-details': hideDetails }">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'TextField',
  props: {
    modelValue: {
      type: [String, Number],
      required: true
    },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    dark: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    styles: { type: Object, default: () => ({}) },
    inputRef: { type: String, default: '' },
    hideDetails: { type: Boolean, default: false },
    noOutline: { type: Boolean, default: false },
    error: { type: String, required: false, default: '' }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'enter', 'keyup'],
  data() {
    return {
      isFocused: false,
      startValidation: false
    };
  },
  mounted() {
    if (this.autofocus) {
      document.getElementById('inputID').focus();
    }
  },
  computed: {
    errorMessage() {
      if (this.error) {
        return this.error;
      }
      if (!this.startValidation || this.disabled) {
        return '';
      }

      let errorMsg = '';
      this.rules.some(rule => {
        const ruleResult = rule(this.modelValue);
        if (typeof ruleResult === 'string' || ruleResult === false) {
          errorMsg = String(ruleResult);
          return true;
        }
      });
      this.$emit('validation', !errorMsg);
      return errorMsg;
    },
    containerClass() {
      let classes = 'text-field-wrapper';

      if (this.dark) {
        classes += ' dark';
      } else {
        classes += ' light';
      }

      if (this.isFocused) {
        classes += ' focused';
      }
      if (this.errorMessage) {
        classes += ' error-state';
      }
      if (this.disabled) {
        classes += ' disabled';
      }
      if (this.readonly) {
        classes += ' readonly';
      }
      if (this.noOutline) {
        classes += ' no-outline';
      }

      return classes;
    }
  },
  methods: {
    emitModelValueUpdate($event) {
      this.$emit('update:modelValue', $event.target.value);
    },
    onFocus(e) {
      this.isFocused = true;
      this.startValidation = true;
      this.$emit('focus', e);
    },
    onBlur(e) {
      this.isFocused = false;
      this.$emit('blur', e);
    },
    onKeyup(e) {
      this.$emit('keyup', e);
    },
    onKeyupEnter(e) {
      this.$emit('enter', e);
    }
  }
};
</script>

<style lang="scss" scoped>
.text-field-wrapper {
  width: 100%;
  .input-and-slots-wrapper {
    border-radius: 4px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    input {
      color: rgba(0, 0, 0, 0.87);
      flex: 1;
      outline: none !important;
      border: none;
      background: transparent;
      height: 38px;
      padding: 0 2px;
      width: 100%;
      min-width: 30px;
    }
    input::placeholder {
      font-size: 14px;
      font-weight: 400;
    }
    .append-slot {
      padding-left: 4px;
    }
    .prepend-slot {
      padding-right: 4px;
    }
  }
  .error-message {
    font-size: 12px;
    height: 14px;
    padding: 0 12px;
    margin-top: 4px;
  }
  .error-message.hide-details {
    display: none;
  }
}
.text-field-wrapper.light {
  .input-and-slots-wrapper {
    border: 1px solid rgba(0, 0, 0, 0.38);
    input {
      color: rgba(0, 0, 0, 0.87);
    }
    input::placeholder {
      color: rgba(0, 0, 0, 0.38);
    }
  }
  &.focused {
    .input-and-slots-wrapper {
      outline: 1px solid #0075ff;
      border-color: #0075ff;
      .append-slot {
        color: #0075ff;
      }
    }
  }
  .error-message {
    color: #ff5252;
  }
}
.text-field-wrapper.dark {
  .input-and-slots-wrapper {
    border: 1px solid #ffffff30;
    input {
      color: #fff;
    }
    input::placeholder {
      color: #fff;
    }
  }
  &.focused {
    .input-and-slots-wrapper {
      outline: 1px solid #fff;
      border-color: #fff;
      .append-slot {
        color: #fff;
      }
    }
  }
  .error-message {
    color: #fff;
  }
}
.text-field-wrapper.error-state {
  .input-and-slots-wrapper {
    outline: 1px solid #ff5252 !important;
    border-color: #ff5252 !important;
    .append-slot {
      color: #ff5252 !important;
    }
  }
}
.text-field-wrapper.disabled {
  .input-and-slots-wrapper {
    background: #fafafa !important;
    input {
      background: transparent;
      cursor: not-allowed;
      pointer-events: all;
    }
  }
}
.text-field-wrapper.readonly {
  .input-and-slots-wrapper {
    input {
      cursor: default;
      pointer-events: none;
    }
  }
}
.text-field-wrapper.no-outline {
  .input-and-slots-wrapper {
    border: none !important;
  }
  &.focused {
    .input-and-slots-wrapper {
      outline: none !important;
    }
  }
}
</style>
